/**
 * ConversationDetailPage — single-Conversation view, reached from the Conversations workspace or a
 * direct link (HELPDESK-CONVERSATIONAL-WORKER-001; resume header added by
 * DIGEMP-GLOBAL-CONVERSATIONS-FRONTEND-072).
 *
 * Thin wrapper: all chat rendering lives in ConversationPanel, the same component Ticket Detail
 * and Agent Detail mount. The 072 addition is a resume header built from the conversation-center
 * thread projection (GET /api/agents/conversation-center/{id}) — the SAME contract and the SAME
 * server assembly the workspace list uses — so a thread opened from the list keeps its identity,
 * ticket/customer context, attention and human-readable status here. When that metadata read
 * fails the page says so and still shows the chat; a partial header is never presented as if it
 * were complete.
 *
 * The read-only CustomerContextPanel above the chat shows which customer/ticket/user/device/target
 * the conversation is bound to (DIGEMP-CUSTOMER-CONTEXT-BINDING-FRONTEND-065).
 */
import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { conversationsApi, conversationCenterApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import ConversationPanel from "../components/ConversationPanel";
import CustomerContextPanel from "../components/CustomerContextPanel";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { attentionBadgeClass, threadTicketCustomer } from "../components/conversationPresentation";
import { conversationStatusBadgeClass } from "../components/conversationStatusVocabulary";

function ThreadHeader({ thread }) {
  const activeEmployees = thread.employees.filter((e) => e.is_active);
  const employees = activeEmployees.length > 0 ? activeEmployees : thread.employees;
  const ticket = thread.ticket;
  // The chip above states the thread's customer. The linked ticket's own customer is a second served
  // fact the projection carried and no surface read, so it is printed beside the ticket identity —
  // and only when it is not the name the chip already states.
  const ticketCustomer = threadTicketCustomer(thread);

  return (
    <div className="bg-white rounded-xl shadow-sm border px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{thread.thread_type_label}</span>
        <span className={`px-2 py-0.5 rounded-full ${attentionBadgeClass(thread.attention)}`}>{thread.attention_label}</span>
        <span className={`px-2 py-0.5 rounded-full ${conversationStatusBadgeClass(thread.status)}`}>{thread.status_label}</span>
        {thread.company_name && (
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">לקוח: {thread.company_name}</span>
        )}
      </div>

      <p className="text-sm text-gray-700">{thread.human_status}</p>
      {ticket && (
        <p className="text-xs text-gray-500">
          קריאה {ticket.ticket_number ? `#${ticket.ticket_number}` : ""} · {ticket.title}
          {ticketCustomer ? <span data-testid="thread-ticket-customer"> · {ticketCustomer}</span> : null}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {employees.length === 0 && <span className="text-xs text-gray-400">ללא עובד משויך</span>}
        {employees.map((e) => (
          <span key={e.agent_id || e.name} className="flex items-center gap-1.5 text-xs text-gray-600">
            <EmployeeAvatar
              name={e.name}
              avatarKey={e.avatar_key}
              avatarColor={e.avatar_color}
              status={e.availability_status}
              isEnabled={e.is_enabled}
              showStatus={Boolean(e.availability_status)}
              size="sm"
            />
            <span className="font-medium">{e.name}</span>
            {e.role_title && <span className="text-gray-400">· {e.role_title}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ConversationDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");
  const [conversation, setConversation] = useState(null);
  const [error, setError] = useState(null);
  // A Manager Console or routing-desk choice opens this page with the request text as a draft the
  // operator still reviews and sends — nothing is posted automatically.
  const consoleRequest =
    typeof location.state?.managerConsoleRequest === "string"
      ? location.state.managerConsoleRequest
      : typeof location.state?.routingRequest === "string"
        ? location.state.routingRequest
        : "";

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    conversationsApi.get(id)
      .then((data) => { if (!cancelled) setConversation(data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || "שיחה לא נמצאה"); });
    return () => { cancelled = true; };
  }, [id, canView]);

  const { data: thread, isError: threadUnavailable } = useQuery({
    queryKey: ["conversation-center-thread", id],
    queryFn: () => conversationCenterApi.get(id),
    enabled: canView && Boolean(id),
    retry: false,
    staleTime: 30000,
  });

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בשיחות AI</div>;
  }

  return (
    <div className="w-full max-w-[900px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-800">שיחת AI</h1>
        <div className="flex items-center gap-3">
          <Link to={`/collaboration/${id}`} className="text-sm text-blue-600 hover:underline">
            תצוגת שיתוף פעולה →
          </Link>
          {conversation?.ticket_id && (
            <Link to={`/tickets/${conversation.ticket_id}?tab=ai-chat`} className="text-sm text-blue-600 hover:underline">
              פתח בכרטיס הקריאה →
            </Link>
          )}
        </div>
      </div>
      {thread && <ThreadHeader thread={thread} />}
      {threadUnavailable && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          לא הצלחנו לטעון את פרטי השיחה (סוג, משתתפים, סטטוס) — ההודעות עצמן מוצגות למטה.
        </p>
      )}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      {conversation && <CustomerContextPanel conversation={conversation} />}
      {conversation && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <ConversationPanel
            conversationId={conversation.id}
            ticketId={conversation.ticket_id}
            customerId={conversation.customer_id}
            initialDraft={consoleRequest}
          />
        </div>
      )}
    </div>
  );
}
